// Words blocks (WEB_V2_BUILD.md §3): the pure side of rendering them. The
// block editor (Tokyo) saves `blocks: [{id, kind, ...}]`; posts and projects
// use the same list plus post-only kinds. Everything here runs under plain
// node (tests/blocks.test.js): no Vue, no import.meta.

import {blockHtml, contentHtml} from "./contentHtml.js";
import {shownImageUrl} from "./sectionMedia.js";

export const WORDS_KINDS = ["text", "heading", "photo", "photos", "photo-text", "quote", "columns", "ticks", "note", "button", "divider", "video", "links"];
export const POST_KINDS = ["callout", "toggle", "gallery", "link-card", "product-card", "file", "signup"];
// before-after: pairs in a project story (Turn a job into a project).
export const PROJECT_KINDS = ["before-after"];
const ALL_KINDS = new Set([...WORDS_KINDS, ...POST_KINDS, ...PROJECT_KINDS]);

const isObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const filled = (v) => typeof v === "string" && v.trim() !== "";

// The blocks a section, post or project draws. A Words section saved before
// blocks (a `content` string) is one text block, through contentHtml as today.
// Unknown kinds and post-only kinds outside a post are left out.
export function normaliseBlocks(record, {allowPost = false} = {}) {
    const list = Array.isArray(record?.blocks) ? record.blocks : null;
    if (list && list.length) {
        return list
            .filter((b) => isObject(b) && ALL_KINDS.has(b.kind) && (allowPost || !POST_KINDS.includes(b.kind)))
            .map((b, i) => ({...b, id: filled(b.id) ? b.id : `b${i}`}));
    }
    if (filled(record?.content)) {
        return [{id: "legacy", kind: "text", html: record.content, markdown: true}];
    }
    return [];
}

// A text-ish block's HTML, made safe. Legacy content goes through markdown.
export function safeBlockHtml(block) {
    if (!block) return "";
    return block.markdown ? contentHtml(block.html) : blockHtml(block.html);
}

// Plain text from HTML (for headings, reading time and the contents list).
export function plainText(html) {
    return String(html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();
}

// Where a link may go: same-site paths, anchors, http(s), mailto and tel.
export function safeLink(url) {
    if (typeof url !== "string") return "";
    const u = url.trim();
    if (!u) return "";
    if (u.startsWith("/") && !u.startsWith("//")) return u;
    if (u.startsWith("#")) return u;
    if (/^(https?:|mailto:|tel:)/i.test(u)) return u;
    return "";
}

export function isExternal(url) {
    return /^https?:\/\//i.test(url || "");
}

export function linkHost(url) {
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch {
        return "";
    }
}

// One photo or video, whatever shape it was saved in:
//   P1/Tokyo media  {type, image:{url,pexels}, video:{link,poster,pexels}, caption}
//   a bare image    {url} / {pexels:{src}} / {attachment:{url}}
//   a string url
// → {kind: 'image'|'video'|'', src, poster, caption}
export function mediaOf(media) {
    if (!media) return {kind: "", src: "", poster: "", caption: ""};
    if (typeof media === "string") return {kind: "image", src: media, poster: "", caption: ""};
    if (!isObject(media)) return {kind: "", src: "", poster: "", caption: ""};
    const caption = filled(media.caption) ? media.caption : "";
    const wantsVideo = media.type === "video" || (!media.image && isObject(media.video));
    if (wantsVideo && isObject(media.video)) {
        const v = media.video;
        const pexelsFile = Array.isArray(v.pexels?.video_files)
            ? (v.pexels.video_files.find((f) => f.quality === "hd") || v.pexels.video_files[0])?.link
            : "";
        const src = filled(v.link) ? v.link : filled(v.url) ? v.url : pexelsFile || "";
        const poster = filled(v.poster) ? v.poster : (v.pexels?.image || "");
        return {kind: src ? "video" : "", src, poster, caption};
    }
    const image = isObject(media.image) ? media.image : media;
    const src = shownImageUrl(image) || (isObject(image.attachment) && filled(image.attachment.url) ? image.attachment.url : "");
    return {kind: src ? "image" : "", src, poster: "", caption};
}

// The Photo and words strip: the Photo first, then the extras (section.images,
// gallery-style entries), up to 3 in all; the Photo alone when nothing else.
export function stripPhotos(section) {
    const extras = Array.isArray(section?.images) ? section.images : [];
    const list = [section?.image, ...extras].filter((m) => mediaOf(m).kind === "image").slice(0, 3);
    return list.length ? list : [section?.image];
}

// A list of media, only the ones with something to show.
export function mediaList(list) {
    return (Array.isArray(list) ? list : []).map(mediaOf).filter((m) => m.kind);
}

// A heading's anchor id, unique within the page.
export function slugify(text) {
    return plainText(text).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "section";
}

// Heading blocks (and h2/h3 inside text blocks) for a contents list, each
// with the id the renderer gives it.
export function contentsOf(blocks) {
    const used = new Map();
    const unique = (base) => {
        const n = used.get(base) || 0;
        used.set(base, n + 1);
        return n ? `${base}-${n + 1}` : base;
    };
    const items = [];
    for (const b of blocks || []) {
        if (b.kind === "heading" && filled(b.text)) {
            const level = Number(b.level) === 3 ? 3 : 2;
            items.push({blockId: b.id, id: unique(slugify(b.text)), text: plainText(b.text), level});
        }
    }
    return items;
}

// Words in the blocks, for reading time when the record doesn't carry it.
export function wordCount(blocks) {
    let text = "";
    for (const b of blocks || []) {
        if (["text", "note", "callout", "toggle"].includes(b.kind)) text += " " + plainText(b.html) + " " + (b.title || "");
        else if (b.kind === "photo-text") text += " " + plainText(b.html);
        else if (b.kind === "heading" || b.kind === "quote") text += " " + plainText(b.text);
        else if (b.kind === "columns") text += " " + (b.cols || []).map((c) => plainText(c?.html)).join(" ");
        else if (b.kind === "ticks") text += " " + (b.items || []).join(" ");
    }
    return text.split(/\s+/).filter(Boolean).length;
}

export function readingMinutes(blocks, given) {
    const n = Number(given);
    if (Number.isFinite(n) && n > 0) return Math.round(n);
    return Math.max(1, Math.round(wordCount(blocks) / 220));
}

// The `align` value as a class suffix.
// Stored as bristol's webBlocks ALIGN (left|centre|right|justify, British
// spelling); "center" is accepted too. Returns the CSS value.
export function alignOf(align) {
    if (align === "centre" || align === "center") return "center";
    return ["right", "justify"].includes(align) ? align : "left";
}

// Photo sizes in a Words block → a max width (CSS value).
export const PHOTO_WIDTH = {s: "40%", m: "62%", l: "82%", full: "100%", wide: "100%"};
